import React, {Fragment, useEffect, useState} from 'react';
import {useDispatch, useSelector} from "react-redux";
import {useTranslation} from "react-i18next";
import {Link} from "react-router-dom";
import Box from "@material-ui/core/Box";
import Chip from "@material-ui/core/Chip";
import Typography from "@material-ui/core/Typography";
import Snackbar from "@material-ui/core/Snackbar";
import Alert from "@material-ui/lab/Alert";
import {
    DndContext,
    closestCenter,
    MouseSensor,
    TouchSensor,
    KeyboardSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    SortableContext,
    verticalListSortingStrategy,
    sortableKeyboardCoordinates,
    arrayMove,
} from '@dnd-kit/sortable';
import {WEB_FAQ_CREATE} from "../../../../../../constants/appUrls.constants";
import {sortFaq, sortFaqClearState} from "../../../../../../redux/actions/Web/Faq/faqActions";
import {getAppLang, isDefined} from "../../../../../../utils";
import faqStyles from "./faqStyles";
import SortableFaqItem from "./SortableFaqItem";

// Listede gosterilecek dili secer: once uygulama dili, sonra tr / en, sonra ilk dolu dil.
const pickLanguageCode = (languages) => {
    if (!Array.isArray(languages) || languages.length === 0) return null;

    const filled = languages.filter(lang => isDefined(lang?.question));
    const pool = filled.length > 0 ? filled : languages;

    const preferred = pool.find(lang => lang?.language_code === getAppLang())
        || pool.find(lang => lang?.language_code === 'tr')
        || pool.find(lang => lang?.language_code === 'en')
        || pool[0];

    return preferred?.language_code;
};

const FaqList = ({list, propertyWebId}) => {
    const {t} = useTranslation();
    const classes = faqStyles();
    const dispatch = useDispatch();

    const sortError = useSelector(state => state.faqReducers?.sortFaqError);
    const sortData = useSelector(state => state.faqReducers?.sortFaq);

    // Surukleme aninda liste yerel state uzerinden gosterilir; kayit arka planda yapilir.
    const [items, setItems] = useState([]);
    const [snackbar, setSnackbar] = useState({open: false, message: '', severity: 'success'});

    const sensors = useSensors(
        useSensor(MouseSensor, {activationConstraint: {distance: 8}}),
        useSensor(TouchSensor, {activationConstraint: {delay: 50, tolerance: 8}}),
        useSensor(KeyboardSensor, {coordinateGetter: sortableKeyboardCoordinates})
    );

    useEffect(() => {
        setItems(Array.isArray(list) ? list : []);
    }, [list]);

    useEffect(() => {
        if (isDefined(sortData?.status) && sortData.status === 200) {
            setSnackbar({open: true, message: t('enw-web-faq-order_saved', 'Sıralama kaydedildi.'), severity: 'success'});
            dispatch(sortFaqClearState());
        }
        if (sortError) {
            setSnackbar({open: true, message: t('enw-error-message'), severity: 'error'});
            dispatch(sortFaqClearState());
        }
    }, [sortData, sortError, dispatch, t]);

    const handleDragEnd = (event) => {
        const {active, over} = event;
        if (!isDefined(over) || active.id === over.id) return;

        const oldIndex = items.findIndex(item => item?.faq_group_id === active.id);
        const newIndex = items.findIndex(item => item?.faq_group_id === over.id);
        if (oldIndex === -1 || newIndex === -1) return;

        const nextItems = arrayMove(items, oldIndex, newIndex)
            .map((item, index) => ({...item, order_number: index + 1}));

        setItems(nextItems);

        dispatch(sortFaq({
            property_web_id: propertyWebId,
            faq_order: nextItems.map(item => ({
                faq_group_id: item?.faq_group_id,
                order_number: item?.order_number,
            })),
        }));
    };

    const isEmpty = items.length === 0;
    const publishedCount = items.filter(item => Number(item?.status) === 1).length;
    const previewCount = items.length - publishedCount;

    return (
        <Fragment>
            {!isEmpty &&
                <Box className={classes.listHeader}>
                    <Box className={classes.listHeaderIcon}>
                        <i className="fas fa-comments"/>
                    </Box>

                    <Box className={classes.listHeaderText}>
                        <Box className={classes.listHeaderTitleRow}>
                            <Typography className={classes.listHeaderCount} component="span">
                                {`${items.length} ${t('enw-web-faq-count_label', 'soru')}`}
                            </Typography>
                            {publishedCount > 0 &&
                                <Chip
                                    size="small"
                                    className={`${classes.statusChip} ${classes.statusPublish}`}
                                    label={`${publishedCount} ${t('enw-web-faq-status_publish', 'Yayında')}`}
                                />
                            }
                            {previewCount > 0 &&
                                <Chip
                                    size="small"
                                    className={`${classes.statusChip} ${classes.statusPreview}`}
                                    label={`${previewCount} ${t('enw-web-faq-status_preview', 'Önizleme')}`}
                                />
                            }
                        </Box>
                        <Typography className={classes.listHeaderDesc}>
                            {t('enw-web-faq-list_desc', 'Sıkça sorulan sorular web sitenizde açılır liste olarak görünür ve arama motorlarına da bu şekilde sunulur.')}
                        </Typography>
                    </Box>

                    <Link to={WEB_FAQ_CREATE} className={classes.primaryBtn}>
                        <i className="fas fa-plus-circle" aria-hidden="true"/> {t('enw-web-faq-create', 'Yeni Soru')}
                    </Link>
                </Box>
            }

            {
                isEmpty ?
                    <Box className={classes.emptyCard}>
                        <Box className={classes.emptyIconWrap}>
                            <i className="fas fa-question"/>
                        </Box>
                        <Typography className={classes.emptyTitle}>
                            {t('enw-web-faq-empty', 'Henüz soru eklenmemiş.')}
                        </Typography>
                        <Typography className={classes.emptyDesc}>
                            {t('enw-web-faq-empty_desc', 'Misafirlerinizin en çok sorduğu soruları ekleyin. Tek dilde yazıp yapay zekâ ile tüm dillere çevirebilirsiniz.')}
                        </Typography>
                        <Link to={WEB_FAQ_CREATE} className={classes.primaryBtn}>
                            <i className="fas fa-plus" aria-hidden="true"/> {t('enw-web-faq-create_first', 'Yeni Soru Ekle')}
                        </Link>
                    </Box>
                    :
                    <Fragment>
                        {items.length > 1 &&
                            <Typography className={classes.dragHint} component="div">
                                <i className="fas fa-arrows"/>
                                {t('enw-web-faq-drag_hint', 'Soruları sürükleyip bırakarak sıralayabilirsiniz; sıra otomatik kaydedilir.')}
                            </Typography>
                        }

                        <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragEnd={handleDragEnd}
                        >
                            <SortableContext
                                items={items.map(item => item?.faq_group_id)}
                                strategy={verticalListSortingStrategy}
                            >
                                {items.map((faq, index) => (
                                    <SortableFaqItem
                                        key={faq?.faq_group_id}
                                        faq={faq}
                                        position={index + 1}
                                        defaultLanguageCode={pickLanguageCode(faq?.languages)}
                                        propertyWebId={propertyWebId}
                                    />
                                ))}
                            </SortableContext>
                        </DndContext>
                    </Fragment>
            }

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar(prev => ({...prev, open: false}))}
                anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
            >
                <Alert
                    onClose={() => setSnackbar(prev => ({...prev, open: false}))}
                    severity={snackbar.severity}
                    variant="filled"
                    style={{borderRadius: 10}}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Fragment>
    );
};

export default FaqList;
